import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { Loader2Icon } from "lucide-react";
import type { VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariantsConfig = {
  variants: {
    variant: {
      default: "imagine-button-primary text-primary-foreground",
      destructive: "imagine-button-destructive text-white",
      outline:
        "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
      secondary: "imagine-button-secondary text-secondary-foreground",
      ghost:
        "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
      link: "text-primary underline-offset-4 hover:underline",
    },
    size: {
      default: "h-9 px-[10px] py-[6px] has-[>svg]:px-[10px]",
      sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
      lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
      icon: "size-9",
    },
  },
  defaultVariants: {
    variant: "default" as const,
    size: "default" as const,
    flat: false as const,
  },
};

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-normal transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  buttonVariantsConfig,
);

function Button({
  className,
  variant,
  size,
  loading = false,
  loadingText,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
    loadingText?: React.ReactNode;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(
        buttonVariants({ variant, size, className }),
        "cursor-pointer transition-all",
      )}
      {...props}
      disabled={!!loading || props.disabled}
    >
      <span className="relative z-10 inline-flex items-center justify-center gap-2">
        {loading ? (
          <>
            <Loader2Icon className="animate-spin" />
            {loadingText ? loadingText : props.children}
          </>
        ) : (
          props.children
        )}
      </span>
    </Comp>
  );
}

export { Button, buttonVariants, buttonVariantsConfig };
